import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Renderer2,
    SimpleChanges,
} from '@angular/core';

@Directive({
    selector: '[hamFlyout]',
})
export class HamFlyoutDirective implements AfterViewInit, OnChanges, OnDestroy {
    @HostBinding('class') className = 'ham-flyout';
    @Output() closeFlyout = new EventEmitter<void>(); // EventEmitter to notify on close
    @Input() position: { top: number; right: number; left?: number }; // Input to set position
    @Input() disableBackdrop = false; // Input to disable backdrop div, which is important for outside-click removal of flyout, but for hover state bad.

    private element: HTMLElement;
    private backdrop: HTMLElement; // Element to close menu when clicked outside
    // To store the references of listeners
    private listeners: (() => void)[] = [];

    constructor(
        private el: ElementRef,
        private renderer: Renderer2, // Inject Renderer2
    ) {}

    ngAfterViewInit(): void {
        this.element = this.el.nativeElement;

        // Apply the initial position to the flyout
        this.updatePosition();

        // Create backdrop element
        this.createBackdrop();

        // Move modal to the global placeholder
        this.moveModalToGlobalPlaceholder();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Check if position has changed and element is available
        if (changes['position'] && this.element) {
            this.updatePosition();
        }
    }

    private updatePosition(): void {
        if (!this.element || !this.position) return;

        // Apply the position to the flyout
        this.renderer.setStyle(this.element, 'position', 'fixed');
        this.renderer.setStyle(this.element, 'top', `${this.position.top}px`);
        this.renderer.setStyle(this.element, 'right', `${this.position.right}px`);

        if (this.position.left !== undefined) {
            this.renderer.setStyle(this.element, 'left', `${this.position.left}px`);
        } else {
            // Remove left style if it's not provided
            this.renderer.removeStyle(this.element, 'left');
        }
    }

    private createBackdrop() {
        this.backdrop = this.renderer.createElement('div');
        this.renderer.setStyle(this.backdrop, 'position', 'fixed');
        this.renderer.setStyle(this.backdrop, 'top', '0');
        this.renderer.setStyle(this.backdrop, 'left', '0');
        this.renderer.setStyle(this.backdrop, 'width', '100vw');
        this.renderer.setStyle(this.backdrop, 'height', '100vh');
        this.renderer.setStyle(this.backdrop, 'z-index', '99'); // Ensure it's behind the menu
        this.renderer.setStyle(this.backdrop, 'background', 'transparent');
        // Make backdrop transparent to hit‑testing so it doesn't steal hover
        if (this.disableBackdrop) {
            this.renderer.setStyle(this.backdrop, 'pointer-events', 'none');
        }

        // Close menu on click
        this.listeners.push(
            this.renderer.listen(this.backdrop, 'click', () => {
                this.closeMenu();
            }),
        );
    }

    private moveModalToGlobalPlaceholder() {
        const placeholder = this.renderer.selectRootElement('#global-flyout-placeholder', true);
        if (placeholder) {
            // Append backdrop first to the global placeholder
            this.renderer.appendChild(placeholder, this.backdrop);

            // Then append the modal element to the global placeholder
            this.renderer.appendChild(placeholder, this.element);
        }
    }

    closeMenu() {
        this.closeFlyout.emit(); // Emit close event to parent

        this.renderer.removeClass(this.element, 'open'); // Remove 'open' class
        this.renderer.removeChild(this.element.parentElement, this.element); // Remove modal
        this.renderer.removeChild(this.backdrop.parentElement, this.backdrop); // Remove backdrop
    }

    ngOnDestroy(): void {
        // Remove modal and backdrop on destruction
        if (this.element) {
            this.renderer.removeChild(this.element.parentElement, this.element);
        }
        if (this.backdrop) {
            this.renderer.removeChild(this.backdrop.parentElement, this.backdrop);
        }
        // Remove all the listeners when the directive is destroyed
        this.listeners.forEach((listener) => listener());
    }
}
